import PolicyApplicationDashboardCard from '@/components/policy-application/PolicyApplicationDashboardCard';
import { useDispatch, useSelector } from 'react-redux';
import React, { useEffect } from 'react';
import {
  selectClient,
  selectPartnerPolicyApplications,
  selectPolicyApplications,
  selectPolicyApplicationsStatus,
  selectSessionAdvisor,
  selectClientCalculationSettings,
} from '@client/model';
import {
  fundingPolicyTypeDefs,
  getPolicyNameParts,
  newUuid,
  formatROI,
  isNullOrUndefined,
} from '@shared';
import { FullScreenLoadingOverlay } from '@client';
import { isPolicyEligibleForApplication } from '@shared/policy-application/policyApplicationDefs';
import {
  putPolicyApplicationForClientByClientIdRequest,
  deletePolicyApplicationByClientIdRequest,
  fetchPolicyApplicationsWithStatusUpdatesForClientByClientIdRequest,
} from '@client/model';
import { useNavigate } from 'react-router';
import PolicyApplicationDashboardPartnerCard from '@client/components/policy-application/PolicyApplicationDashboardPartnerCard';

type FundingMetricsStrict = {
  totalCost: number | undefined;
  ltcPayout: number | undefined;
  nonLtcPayout: number | undefined;
  totalPayout: number | undefined;
  projectedReturnOnInvestment: number | undefined;
  projectedCompoundAnnualGrowthRate: number | undefined;
  projectedInternalRateOfReturn: number | undefined;
  investmentPeriodYears: number | undefined;
  formattedROI: string | undefined;
  isDeactivated: boolean | undefined;
  quoteOutdatedReason: string | undefined;
  roiCalculationType: ReturnOnInvestmentCalculationType | undefined;
};

const nn = <T,>(v: T | null | undefined): T | undefined => v ?? undefined;

function buildPolicyRoiInput(policy: PolicyFundingSource) {
  return {
    projectedReturnOnInvestment: policy.policyProjectedReturnOnInvestment ?? 0,
    projectedCompoundAnnualGrowthRate: policy.policyProjectedCompoundAnnualGrowthRate ?? 0,
    projectedInternalRateOfReturn: policy.policyProjectedInternalRateOfReturn,
    investmentPeriodYears: policy.investmentPeriodYears ?? 0,
    isIrrSuspect: policy.isIrrSuspect,
    quoteOutdatedReason: policy.quoteOutdatedReason,
  };
}

function formatPolicyRoi(
  policy: PolicyFundingSource,
  calculationType?: ReturnOnInvestmentCalculationType,
): string | undefined {
  if (isNullOrUndefined(policy.policyProjectedReturnOnInvestment)) {
    return undefined;
  }

  return formatROI(buildPolicyRoiInput(policy), calculationType || 'total');
}

export function deriveApplicationStatus(policyApplication: ClientPolicyApplication) {
  const isStatusNotStarted = policyApplication.steps.every(
    step => step.status === 'not_started' || step.status === 'blocked',
  );
  const isStatusCompleted = policyApplication.steps.every(
    step => step.status === 'completed' || step.status === 'skipped',
  );
  const isStatusInProgress = !isStatusNotStarted && !isStatusCompleted;
  const status = isStatusNotStarted
    ? 'not_started'
    : isStatusInProgress
      ? 'in_progress'
      : isStatusCompleted
        ? 'completed'
        : 'not_started';

  const progressPercent = isStatusCompleted
    ? 100
    : isStatusInProgress
      ? Math.round(
          (policyApplication.steps.filter(
            step => step.status === 'completed' || step.status === 'skipped',
          ).length /
            policyApplication.steps.length) *
            100,
        )
      : 0;

  return { status, progressPercent };
}

function getMostRecentFormUpdatedDateTime(policyApplication: ClientPolicyApplication): string {
  let mostRecent: Date | null = null;

  for (const step of policyApplication.steps) {
    const dt = step.clientPolicyApplicationForm?.updatedDateTime;
    if (dt) {
      const date = new Date(dt);
      if (!mostRecent || date > mostRecent) {
        mostRecent = date;
      }
    }
  }

  return mostRecent?.toISOString() || '';
}

export function createApplicationCardData(
  policyApplications: ClientPolicyApplication[],
  multipleFundingSources?: MultipleFundingSources,
  clientHasStartedLtc?: boolean,
  returnOnInvestmentCalculationType?: ReturnOnInvestmentCalculationType,
): ApplicationCardData[] {
  return policyApplications
    .map(policyApplication => {
      const fundingSourceId = policyApplication.fundingSourceId;
      const fundingPolicyType = policyApplication.fundingPolicyType;
      const linkedPolicy =
        multipleFundingSources && fundingSourceId
          ? multipleFundingSources.ltcPolicy[fundingSourceId]
          : undefined;
      const policyDisplayName = linkedPolicy
        ? getPolicyNameParts(linkedPolicy).primary
        : fundingPolicyTypeDefs[fundingPolicyType].label;
      const title = policyDisplayName;
      const carrier = fundingPolicyTypeDefs[fundingPolicyType].carrier;
      const product = policyDisplayName;
      const { status, progressPercent } = deriveApplicationStatus(policyApplication);

      let fundingMetrics: FundingMetricsStrict = {
        totalCost: undefined,
        ltcPayout: undefined,
        nonLtcPayout: undefined,
        totalPayout: undefined,
        projectedReturnOnInvestment: undefined,
        projectedCompoundAnnualGrowthRate: undefined,
        projectedInternalRateOfReturn: undefined,
        investmentPeriodYears: undefined,
        formattedROI: undefined,
        isDeactivated: undefined,
        quoteOutdatedReason: undefined,
        roiCalculationType: undefined,
      };

      if (multipleFundingSources && fundingSourceId) {
        const policy = multipleFundingSources.ltcPolicy[fundingSourceId];
        if (policy) {
          fundingMetrics = {
            totalCost: nn(policy.policyTotalCost),
            ltcPayout: nn(
              clientHasStartedLtc
                ? policy.policyCoverageOfRemainingLtcCosts
                : policy.projectedCostCoverage,
            ),
            nonLtcPayout: nn(policy.policyNonLtcPayout),
            totalPayout: nn(policy.policyTotalValue),
            projectedReturnOnInvestment: nn(policy.policyProjectedReturnOnInvestment),
            projectedCompoundAnnualGrowthRate: nn(policy.policyProjectedCompoundAnnualGrowthRate),
            projectedInternalRateOfReturn: nn(policy.policyProjectedInternalRateOfReturn),
            investmentPeriodYears: nn(policy.investmentPeriodYears),
            formattedROI: formatPolicyRoi(policy, returnOnInvestmentCalculationType),
            roiCalculationType: returnOnInvestmentCalculationType,
            isDeactivated: nn(policy.isDeactivated),
            quoteOutdatedReason: nn(policy.quoteOutdatedReason),
          };
        }
      }

      return {
        clientPolicyApplicationId: policyApplication.clientPolicyApplicationId,
        title,
        carrier,
        product,
        fundingSourceId,
        fundingPolicyType,
        stateOfResidenceAbbreviation: policyApplication.stateOfResidenceAbbreviation,
        status,
        progress: progressPercent,
        lastUpdated: getMostRecentFormUpdatedDateTime(policyApplication),
        ...fundingMetrics,
        roiCalculationType: returnOnInvestmentCalculationType,
      };
    })
    .filter(Boolean) as ApplicationCardData[];
}

export function createCombinedApplicationCardData(
  multipleFundingSources: MultipleFundingSources,
  applicationCards: ApplicationCardData[],
  clientHasStartedLtc?: boolean,
  returnOnInvestmentCalculationType?: ReturnOnInvestmentCalculationType,
): ApplicationCardData[] {
  const existingPolicyFundingSourceIds = new Set(
    applicationCards.map(card => card.fundingSourceId),
  );
  const notStartedApplications: ApplicationCardData[] = [];

  Object.entries(multipleFundingSources.ltcPolicy).forEach(
    ([fundingSourceId, policyFundingSource]) => {
      const policyType = policyFundingSource.policyType;

      if (policyType) {
        if (policyType && isPolicyEligibleForApplication(policyFundingSource)) {
          if (!existingPolicyFundingSourceIds.has(fundingSourceId)) {
            const policyDef = fundingPolicyTypeDefs[policyType];

            if (policyDef) {
              const policyDisplayName = getPolicyNameParts(policyFundingSource).primary;
              const newApplicationCard: ApplicationCardData = {
                clientPolicyApplicationId: newUuid(),
                title: policyDisplayName,
                carrier: policyDef.carrier || '',
                product: policyDisplayName,
                fundingPolicyType: policyType,
                fundingSourceId: fundingSourceId,
                status: 'not_created',
                progress: 0,
                lastUpdated: '',
                // Add funding metrics for not-created applications
                totalCost: policyFundingSource.policyTotalCost ?? undefined,
                ltcPayout: clientHasStartedLtc
                  ? (policyFundingSource.policyCoverageOfRemainingLtcCosts ?? undefined)
                  : (policyFundingSource.projectedCostCoverage ?? undefined),
                nonLtcPayout: policyFundingSource.policyNonLtcPayout ?? undefined,
                totalPayout: policyFundingSource.policyTotalValue ?? undefined,
                projectedReturnOnInvestment:
                  policyFundingSource.policyProjectedReturnOnInvestment ?? undefined,
                projectedCompoundAnnualGrowthRate:
                  policyFundingSource.policyProjectedCompoundAnnualGrowthRate ?? undefined,
                projectedInternalRateOfReturn:
                  policyFundingSource.policyProjectedInternalRateOfReturn ?? undefined,
                investmentPeriodYears: policyFundingSource.investmentPeriodYears ?? undefined,
                isDeactivated: policyFundingSource.isDeactivated ?? undefined,
                quoteOutdatedReason: policyFundingSource.quoteOutdatedReason ?? undefined,
                // Only format ROI if we have valid values
                formattedROI: formatPolicyRoi(
                  policyFundingSource,
                  returnOnInvestmentCalculationType,
                ),
                roiCalculationType: returnOnInvestmentCalculationType,
              };
              notStartedApplications.push(newApplicationCard);
              existingPolicyFundingSourceIds.add(fundingSourceId);
            }
          }
        }
      }
    },
  );

  return [...applicationCards, ...notStartedApplications];
}

export function PolicyApplicationDashboard() {
  const client = useSelector(selectClient);
  const advisor = useSelector(selectSessionAdvisor);
  const policyApplications = useSelector(selectPolicyApplications);
  const partnerPolicyApplications = useSelector(selectPartnerPolicyApplications);
  const policyApplicationsStatus = useSelector(selectPolicyApplicationsStatus);
  const clientCalculationSettings = useSelector(selectClientCalculationSettings);
  const clientHasStartedLtc = client?.intakeSurvey?.clientHasStartedLtc || false;
  const returnOnInvestmentCalculationType =
    clientCalculationSettings?.returnOnInvestmentCalculationType || 'total';

  const alreadyStartedApplicationCards = createApplicationCardData(
    policyApplications,
    client.multipleFundingSources,
    clientHasStartedLtc,
    returnOnInvestmentCalculationType,
  );
  const alreadyStartedPartnerApplicationCards = createApplicationCardData(
    partnerPolicyApplications,
    client.mutableClientPartner?.multipleFundingSources,
    client.mutableClientPartner?.intakeSurvey?.clientHasStartedLtc,
    returnOnInvestmentCalculationType,
  );
  const applicationCards = createCombinedApplicationCardData(
    client.multipleFundingSources,
    alreadyStartedApplicationCards,
    clientHasStartedLtc,
    returnOnInvestmentCalculationType,
  );
  const sortedApplicationCards = applicationCards.slice().sort((a, b) => {
    if (a.status === 'in_progress' && b.status !== 'in_progress') return -1;
    if (a.status !== 'in_progress' && b.status === 'in_progress') return 1;

    if (a.status === 'in_progress' && b.status === 'in_progress') {
      return (b.progress ?? 0) - (a.progress ?? 0);
    }

    return 0;
  });

  const isLoading = policyApplicationsStatus === 'fetching';
  const hasError = policyApplicationsStatus === 'error';
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    if (client?.clientId) {
      dispatch(
        fetchPolicyApplicationsWithStatusUpdatesForClientByClientIdRequest({
          clientId: client.clientId,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.clientId]);

  const navigateToApplication = (clientPolicyApplicationId: string) => {
    navigate(`/clients/${client.clientId}/policy-application/${clientPolicyApplicationId}`);
  };

  const handleInsertPolicyApplication = (
    clientPolicyApplicationId: string,
    policyType: FundingPolicyType,
    fundingSourceId: string,
    stateOfResidenceAbbreviation: string,
  ) => {
    dispatch(
      putPolicyApplicationForClientByClientIdRequest({
        clientPolicyApplicationId,
        clientId: client.clientId,
        advisorId: client.advisorId,
        partnerClientId: client.partnerClientId,
        fundingPolicyType: policyType,
        fundingSourceId: fundingSourceId,
        stateOfResidenceAbbreviation,
        status: 'not_started',
      }),
    );
  };

  const handleDeletePolicyApplication = (clientPolicyApplicationId: string) => {
    dispatch(
      deletePolicyApplicationByClientIdRequest({
        clientId: client.clientId,
        clientPolicyApplicationId,
      }),
    );
  };

  if (isLoading) {
    return <FullScreenLoadingOverlay isLoading={isLoading} />;
  }

  if (hasError) {
    return (
      <div className="flex  items-center justify-center ">
        <div className="text-center">
          <h2 className="mb-2 text-xl font-semibold text-gray-900">Error loading applications</h2>
          <p className="text-gray-700">Please try again later.</p>
        </div>
      </div>
    );
  }

  return (
    <div className=" pt-4">
      <div className="mx-auto max-w-2xl">
        <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-1">
          {sortedApplicationCards?.map(application => (
            <PolicyApplicationDashboardCard
              key={application.clientPolicyApplicationId}
              application={application}
              clientId={client.clientId}
              navigateToApplication={navigateToApplication}
              handleInsertPolicyApplication={handleInsertPolicyApplication}
              handleDeletePolicyApplication={handleDeletePolicyApplication}
            />
          ))}
          {alreadyStartedPartnerApplicationCards?.map((application, idx) => (
            <PolicyApplicationDashboardPartnerCard
              key={idx}
              application={application}
              partnerClientId={client.partnerClientId as string}
            />
          ))}
        </div>

        {applicationCards?.length === 0 && (
          <div className="py-12 text-center">
            <div className="rounded-lg border bg-white p-8 shadow-sm">
              <h3 className="mb-2 text-lg font-medium text-gray-900">No applications found</h3>
              <p className="text-gray-700">There are no applications for this client yet.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
